import * as React from 'react';
import * as fileHelper from '../../business/utils/file-helper';

import { ApplicationState, ConnectedReduxProps } from '../../store';
import Agenda from '../../components/details/pages/agenda/agenda';
import Case from '../../components/details/pages/case/case';
import { CaseResponse } from '../../store/case/model';
import CommentOrReport from '../../components/details/pages/comment-or-report/comment-or-report';
import { DetailsSharedActionPage } from '../../store/details/shared/model';
import DetailsSideNav from '../../components/details/side-nav/side-nav';
import { Dispatch } from 'redux';
import ExpertiseAttachment from '../../components/details/pages/expertise-attachment/expertise-attachment';
import Footer from '../../components/common/footer';
import Header from '../../components/common/header';
import HonoraryNote from '../../components/details/pages/honorary-note/honorary-note';
import Images from '../../components/details/pages/images/images';
import InsuredPerson from '../../components/details/pages/insured-person/insured-person';
import OtherAttachments from '../../components/details/pages/other-attachments/other-attachments';
import Overview from '../../components/details/pages/overview/overview';
import Policy from '../../components/details/pages/policy/policy';
import Rim from '../../components/details/pages/rim/rim';
import Svs from '../../components/details/pages/svs/svs';
import { connect } from 'react-redux';
import { fetchCaseView } from '../../store/details/case/actions';
import styles from './Details.module.css';
import withParams from '../../components/common/withParams';
import { Params } from 'react-router-dom';
import { getDownloadLink } from '../../store/shared/actions';

interface PropsFromState {
  readonly selectedCase: CaseResponse;
  readonly currentPage: DetailsSharedActionPage;
  readonly roles: string[];
  readonly downloadLinks: Record<string, string>;
}

interface PropsFromDispatch {
  readonly fetchCaseView: typeof fetchCaseView;
  readonly getDownloadLink: typeof getDownloadLink;
}

interface WithParamsProps {
  params: Params<string>;
}

export const setClickedKey = (key: string) => ({
  type: 'SET_CLICKED_KEY',
  payload: key,
});

type AllProps = PropsFromState & PropsFromDispatch & ConnectedReduxProps & WithParamsProps & {
  params: {
    instructionId: number;
  };
};

interface State {
  readonly selectedCase: CaseResponse;
}

class Details extends React.Component<AllProps, State> {
  private clickLinkRef: React.RefObject<HTMLAnchorElement | null>;
  private instructionId: number;

  constructor(props: AllProps) {
    super(props);
    this.instructionId = Number(this.props.params.instructionId);
    this.clickLinkRef = React.createRef<HTMLAnchorElement>();
  }

  onClickLink = (href: string, content: string, filename: string) => {
    if (content !== "") {
      const blob = this.dataURItoBlob(content, filename);
      this.triggerDownload(blob, filename);
      return;
    }
    const element = this.clickLinkRef.current as HTMLAnchorElement;
    element.href = href;
    element.click();
  };

  handleDownloadClick = (amazonKey: string, filename: string) => {
    const { downloadLinks, getDownloadLink } = this.props;

    if (downloadLinks[amazonKey]) {
      // URL is already fetched; trigger download directly
      this.onClickLink(downloadLinks[amazonKey], '', filename);
    } else {
      // Dispatch Redux action to fetch the URL from the back end
      getDownloadLink(amazonKey, '', '', filename);
    }
  };

  triggerDownload(blob: Blob, filename: string) {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.style.display = 'none';
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    URL.revokeObjectURL(url);
    a.remove();
  }

  dataURItoBlob(content: string, filename: string): Blob {
    if (content && content !== undefined) {
      const byteString = atob(content);
      const mimeString = fileHelper.getFileContentType(filename);
      const ia = new Uint8Array(byteString.length);
      for (let i = 0; i < byteString.length; i++) {
        ia[i] = byteString.charCodeAt(i);
      }
      return new Blob([ia], { type: mimeString });
    }
    return new Blob();
  }

  componentDidMount() {
    this.props.fetchCaseView(this.instructionId);
  }

  componentDidUpdate(previousProps: AllProps) {}

  private get selectedPage() {
    switch (this.props.currentPage) {
      case DetailsSharedActionPage.INSURED_PERSON:
        return <InsuredPerson {...this.props} />;
      case DetailsSharedActionPage.CASE:
        return <Case {...this.props} />;
      case DetailsSharedActionPage.EXPERTISE_ATTACHMENT:
        return (
          <ExpertiseAttachment
            {...this.props}
            handleDownloadClick={this.handleDownloadClick} 
          />
        );
      case DetailsSharedActionPage.HONORARY_NOTE:
        return <HonoraryNote {...this.props} />;
      case DetailsSharedActionPage.IMAGES:
        return <Images {...this.props} />;
      case DetailsSharedActionPage.OVERVIEW:
        return (
          <Overview
            {...this.props}
            handleDownloadClick={this.handleDownloadClick} 
          />
        );
      case DetailsSharedActionPage.POLICY:
        return <Policy {...this.props} />;
      case DetailsSharedActionPage.OTHER_ATTACHMENTS:
        return (
          <OtherAttachments
            {...this.props}
            handleDownloadClick={this.handleDownloadClick} 
          />
        );
      case DetailsSharedActionPage.RIM:
        return <Rim {...this.props} />;
      case DetailsSharedActionPage.SVS:
        return <Svs {...this.props} />;
      case DetailsSharedActionPage.AGENDA:
        return <Agenda {...this.props} />;
      case DetailsSharedActionPage.COMMENT_OR_REPORT:
        return (
          <CommentOrReport {...this.props} handleDownloadClick={this.handleDownloadClick} />
        );
      default:
        return null;
    }
  }

  public render() {
    const HeaderWithParams = withParams(Header);

    return (
      <div className={styles.container}>
        <HeaderWithParams {...this.props} />
        <div className={styles.content}>
          <div className={styles.sideNav}>
            <DetailsSideNav {...this.props} />
          </div>
          <div className={styles.sideNavContent}>{this.selectedPage}</div>
        </div>
        <Footer {...this.props} />
        <a
          ref={this.clickLinkRef}
          target="_blank"
          aria-label="Hidden link for dynamic rendering"
          hidden={true}
        />
      </div>
    );
  }
}

const mapStateToProps = (
  { caseState, details: { detailsShared }, shared }: ApplicationState
): PropsFromState => ({
  selectedCase: caseState.selectedCase,
  currentPage: detailsShared.currentPage,
  roles: shared.roles,
  downloadLinks: shared.downloadLinks
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
  fetchCaseView: (instructionId: number) => dispatch(fetchCaseView(instructionId)),
  getDownloadLink: (amazonKey: string, url: string, content: string, filename: string) =>
    dispatch(getDownloadLink(amazonKey, url, content, filename)),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withParams(Details));
